import React, { useCallback, useEffect, useState } from 'react';
import { Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import toastr from 'toastr';
import moment from 'moment';
import { ArrowLeft, Mail } from 'lucide-react';
import { Card } from 'ktm-ui';
import CustomPagination from 'components/CustomPagination';
import ConfirmActionModal from 'components/ConfirmActionModal';
import ChangeAccessTypeModal from '../FirmUserAccess/ChangeAccessTypeModal';
import RevokeFirmAccessModal from '../FirmUserAccess/RevokeFirmAccessModal';
import { describeLogEntry } from '../FirmUserAccess/firmUserAccessLog';
import { formatFirmName } from '../FirmUserAccess/firmName';
import {
  accessTagStyle, toFirmDistro, toFirmUser, UserAccessAccount, UserAccessHistoryEntry, UserAccessProfile,
} from './userAccess';
import '../../styles/CustomComponents.css';
import '../../styles/Employee.css';

type Props = { userId: string; onBack: () => void };

const LOG_PER_PAGE = 8;

const headerButtonStyle: React.CSSProperties = {
  display: 'inline-flex', flexDirection: 'row', alignItems: 'center', gap: 6,
  height: 30, padding: '0 12px', whiteSpace: 'nowrap', width: 'auto',
};

const centeredMessageStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80,
};

const emailPillStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', background: '#f5f5f4',
  border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: 20, fontSize: 12, color: '#6b6b67', marginBottom: 16,
};

const UserAccessManagePage = ({ userId, onBack }: Props) => {
  const [profile, setProfile] = useState<UserAccessProfile | null>(null);
  const [log, setLog] = useState<UserAccessHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [logPage, setLogPage] = useState(1);
  const [showLog, setShowLog] = useState(true);
  const [changeAccount, setChangeAccount] = useState<UserAccessAccount | null>(null);
  const [revokeAccount, setRevokeAccount] = useState<UserAccessAccount | null>(null);
  const [welcomeAccount, setWelcomeAccount] = useState<UserAccessAccount | null>(null);

  const load = useCallback(async () => {
    try {
      const [profileRes, logRes] = await Promise.all([
        axios.get(`/api/Advisors/user/${userId}/access`),
        axios.get(`/api/Advisors/user/${userId}/useraccesslog`),
      ]);
      setProfile(profileRes.data);
      setLog(logRes.data || []);
      setLogPage(1);
    } catch {
      toastr.error('Failed to load user access.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const closeAndMaybeReload = (setter: (v: null) => void) => (changed: boolean) => {
    setter(null);
    if (changed) load();
  };

  const sendWelcome = async () => {
    const account = welcomeAccount;
    setWelcomeAccount(null);
    if (!account || !profile) return;
    try {
      await axios.post(`/api/Advisors/distro/${account.distroId}/sendwelcome`, { email: profile.email });
      toastr.success(`Sent welcome email to ${profile.email}.`);
      load();
    } catch {
      toastr.error(`Failed to send welcome email to ${profile.email}.`);
    }
  };

  const actionLink = (label: string, onClick: () => void, danger = false) => (
    <a
      href="#"
      className="RT-Link"
      style={danger ? { color: 'red' } : undefined}
      onClick={e => { e.preventDefault(); onClick(); }}
    >
      {label}
    </a>
  );

  const renderActions = (a: UserAccessAccount) => {
    if (a.kind === 'advisor') {
      return <span className="text-muted" style={{ fontSize: 12 }}>Owned by this user, can't be changed or removed</span>;
    }
    const links = [actionLink('Send Welcome', () => setWelcomeAccount(a))];
    if (a.distroAccountUserId && (!a.isOwner || a.isClientOnly)) links.push(actionLink('Change Access Type', () => setChangeAccount(a)));
    if (a.distroAccountUserId && !a.isOwner) links.push(actionLink('Remove', () => setRevokeAccount(a), true));
    return links.map((link, i) => (
      <React.Fragment key={i}>
        {i > 0 && <span className="pa-action-sep">|</span>}
        {link}
      </React.Fragment>
    ));
  };

  if (loading) {
    return <div style={{ ...centeredMessageStyle, height: 200 }}><Spinner animation="border" variant="primary" /></div>;
  }

  if (!profile) {
    return (
      <div>
        <button className="RT-Button RT-Button--cancel" onClick={onBack} style={headerButtonStyle}>
          <ArrowLeft size={14} style={{ display: 'inline-block', flexShrink: 0, marginRight: 0 }} />
          <span>Back</span>
        </button>
      </div>
    );
  }

  const lastLog = logPage * LOG_PER_PAGE;
  const firstLog = lastLog - LOG_PER_PAGE;
  const logRows = log.slice(firstLog, lastLog);
  const logPages = Math.ceil(log.length / LOG_PER_PAGE);

  const accountsContent = profile.accounts.length === 0 ? (
    <div style={{ ...centeredMessageStyle, height: 120 }}><span style={{ fontWeight: 'bold' }}>This user no longer has access to any accounts.</span></div>
  ) : (
    <Table hover className="ktm-table pa-table" style={{ backgroundColor: 'white', width: '100%', marginBottom: 0 }}>
      <thead>
        <tr>
          <th>Account</th>
          <th>Access Type</th>
          <th className="pa-col-actions">Actions</th>
        </tr>
      </thead>
      <tbody>
        {profile.accounts.map((a, i) => (
          <tr key={i}>
            <td>
              <div style={{ fontWeight: 500 }}>{a.name}</div>
              {a.sub && <div className="text-muted" style={{ fontSize: 11 }}>{a.sub}</div>}
            </td>
            <td><span style={accessTagStyle}>{a.accessTypeLabel}</span></td>
            <td className="pa-col-actions">{renderActions(a)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const historyContent = log.length === 0 ? (
    <div style={centeredMessageStyle}><span style={{ fontWeight: 'bold' }}>No user access changes recorded yet.</span></div>
  ) : (
    <div className="history-content">
      <Table hover className="ktm-table pa-table" style={{ backgroundColor: 'white', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 170 }}>Date</th>
            <th style={{ width: 220 }}>Account</th>
            <th style={{ width: 170 }}>Action</th>
            <th>Change</th>
            <th style={{ width: 170 }}>Changed By</th>
          </tr>
        </thead>
        <tbody>
          {logRows.map(entry => {
            const d = describeLogEntry(entry);
            return (
              <tr key={entry.id}>
                <td>{moment(entry.createdAt).format('YYYY-MM-DD HH:mm:ss')}</td>
                <td>{formatFirmName({ name: entry.accountName, healthClaimDistroId: entry.healthClaimDistroId })}</td>
                <td>{d.action}</td>
                <td>{d.details}</td>
                <td>{entry.performedBy ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      {logPages > 1 && (
        <div className="pagination-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, marginBottom: 10 }}>
          <div className="pagination-info">
            <span>Showing {firstLog + 1} to {Math.min(lastLog, log.length)} of {log.length} entries</span>
          </div>
          <CustomPagination nPages={logPages} currentPage={logPage} setCurrentPage={setLogPage} />
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <button className="RT-Button RT-Button--cancel" onClick={onBack} style={headerButtonStyle}>
          <ArrowLeft size={14} style={{ display: 'inline-block', flexShrink: 0, marginRight: 0 }} />
          <span>Back</span>
        </button>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{`${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim()}</span>
        <span style={accessTagStyle}>{profile.userType}</span>
      </div>
      <div style={emailPillStyle}><Mail size={12} /> {profile.email}</div>

      <div style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>Account access to</span>{' '}
        <span className="text-muted">({profile.accounts.length})</span>
      </div>

      {accountsContent}

      <div style={{ marginTop: 8 }}>
        <Card
          label="User Access History"
          actions={
            <button onClick={() => setShowLog(v => !v)} className={`toggle-button ${showLog ? 'open' : ''}`}>
              <span className="arrow"></span>
            </button>
          }
        >
          {showLog && historyContent}
        </Card>
      </div>

      {changeAccount && (
        <ChangeAccessTypeModal distro={toFirmDistro(changeAccount)} user={toFirmUser(profile, changeAccount)} onClose={closeAndMaybeReload(setChangeAccount)} />
      )}
      {revokeAccount && (
        <RevokeFirmAccessModal distro={toFirmDistro(revokeAccount)} user={toFirmUser(profile, revokeAccount)} onClose={closeAndMaybeReload(setRevokeAccount)} />
      )}
      {welcomeAccount && (
        <ConfirmActionModal
          title="Send welcome email"
          message={`This will immediately send the welcome email to ${profile.email}. Would you like to proceed?`}
          confirmLabel="Send Email"
          onConfirm={sendWelcome}
          onCancel={() => setWelcomeAccount(null)}
        />
      )}
    </div>
  );
};

export default UserAccessManagePage;
